import type { GallerySnapshot, ResearchLineageNodeProjection, RunProjection } from "@roboscientist/contracts";
import { layoutResearchLineage, lineageRefKey } from "../../web/src/components/researchLineageLayout.js";
import { runObservedAt } from "../../web/src/components/researchLineageEvidence.js";

export type GalleryReplayStage = { depth: number; keys: string[]; start: number; end: number };
export type GalleryReplayPlan = {
  duration: number;
  stages: GalleryReplayStage[];
  nodes: Map<string, { appear: number; settle: number }>;
  runs: Map<string, number>;
};

/** Presentation timing follows the saved DAG. It never edits persisted Run lifecycles. */
export function buildGalleryReplayPlan(snapshot: Pick<GallerySnapshot, "lineage" | "runs">): GalleryReplayPlan {
  const layout = layoutResearchLineage(snapshot.lineage.nodes);
  const depths = [...new Set(layout.nodes.map((node) => node.depth))].sort((a, b) => a - b);
  const stageDuration = Math.max(1600, Math.min(2800, 42000 / Math.max(1, depths.length)));
  const stages = depths.map((depth, index) => ({
    depth,
    keys: layout.nodes.filter((node) => node.depth === depth).sort((a, b) => a.x - b.x || a.y - b.y || a.key.localeCompare(b.key)).map((node) => node.key),
    start: 500 + index * stageDuration,
    end: 500 + (index + 1) * stageDuration
  }));
  const duration = stages.length ? stages.at(-1)!.end + 350 : 0;
  const nodes: GalleryReplayPlan["nodes"] = new Map();
  const runStages = new Map<string, number>();
  const originalNodes = new Map(snapshot.lineage.nodes.map((node) => [lineageRefKey(node.ref), node]));
  const allRuns = new Map<string, RunProjection>();
  for (const node of snapshot.lineage.nodes) for (const evidence of node.runEvidence) allRuns.set(evidence.run.id, evidence.run);
  for (const run of [...snapshot.lineage.unassignedRuns, ...snapshot.lineage.orphanedRuns, ...snapshot.runs]) allRuns.set(run.id, run);
  stages.forEach((stage, stageIndex) => {
    stage.keys.forEach((key, index) => {
      const stagger = index / Math.max(1, stage.keys.length - 1) * 450;
      nodes.set(key, { appear: stage.start + stagger, settle: stage.end - 400 + stagger * .5 });
      for (const entry of originalNodes.get(key)!.runEvidence) {
        if (!runStages.has(entry.run.id)) runStages.set(entry.run.id, stageIndex);
      }
    });
  });
  const runs: GalleryReplayPlan["runs"] = new Map();
  const orderedRuns = [...allRuns.values()].sort((a, b) => runObservedAt(a) - runObservedAt(b) || a.id.localeCompare(b.id));
  stages.forEach((stage, stageIndex) => {
    const boundRuns = orderedRuns.filter((run) => runStages.get(run.id) === stageIndex);
    boundRuns.forEach((run, index) => runs.set(run.id,
      stage.start + 550 + (index + 1) / Math.max(1, boundRuns.length) * (stageDuration - 1100)
    ));
  });
  const unbound = orderedRuns.filter((run) => !runStages.has(run.id));
  unbound.forEach((run, index) => runs.set(run.id, duration * (index + 1) / Math.max(1, unbound.length)));
  return { duration, stages, nodes, runs };
}

/** Add gentle acceleration within each generation while keeping motion continuous between steps.
 * This is a pure clock transform: seeking and offline rendering produce the same saved evidence.
 */
export function easeGalleryReplayElapsed(plan: GalleryReplayPlan, elapsed: number): number {
  const time = Math.max(0, Math.min(plan.duration, elapsed));
  const stage = plan.stages.find((entry) => time >= entry.start && time < entry.end);
  if (!stage) return time;
  const phase = (time - stage.start) / (stage.end - stage.start);
  const curved = phase ** 3 * (phase * (phase * 6 - 15) + 10);
  // Preserve a nonzero cruise velocity at boundaries instead of stopping every generation.
  const eased = .75 * phase + .25 * curved;
  return stage.start + (stage.end - stage.start) * eased;
}

export function projectGalleryReplay(snapshot: Pick<GallerySnapshot, "lineage" | "runs">, plan: GalleryReplayPlan, elapsed: number) {
  const complete = elapsed >= plan.duration;
  const time = Math.max(0, elapsed);
  const visibleKeys = new Set([...plan.nodes].filter(([, timing]) => complete || timing.appear <= time).map(([key]) => key));
  const activeKeys = new Set([...plan.nodes].filter(([, timing]) => !complete && timing.appear <= time && timing.settle > time).map(([key]) => key));
  const shownRunIds = new Set([...plan.runs].filter(([, at]) => complete || at <= time).map(([id]) => id));
  const settled = (key: string) => visibleKeys.has(key) && !activeKeys.has(key);
  const nodes: ResearchLineageNodeProjection[] = complete ? snapshot.lineage.nodes : snapshot.lineage.nodes
    .filter((node) => visibleKeys.has(lineageRefKey(node.ref)))
    .map((node) => {
      const done = settled(lineageRefKey(node.ref));
      return {
        ...node,
        childRefs: node.childRefs.filter((ref) => visibleKeys.has(lineageRefKey(ref))),
        latestAssessment: done ? node.latestAssessment : null,
        assessments: done ? node.assessments : [],
        currentSelection: done ? node.currentSelection : null,
        selectionHistory: done ? node.selectionHistory : [],
        runEvidence: node.runEvidence.filter((entry) => shownRunIds.has(entry.run.id))
      };
    });
  const lineage = complete ? snapshot.lineage : {
    ...snapshot.lineage,
    nodes,
    championRefs: snapshot.lineage.championRefs.filter((ref) => settled(lineageRefKey(ref))),
    championLineageRefs: snapshot.lineage.championLineageRefs.filter((ref) => settled(lineageRefKey(ref))),
    frontierRefs: snapshot.lineage.frontierRefs.filter((ref) => settled(lineageRefKey(ref))),
    unassignedRuns: snapshot.lineage.unassignedRuns.filter((run) => shownRunIds.has(run.id)),
    orphanedRuns: snapshot.lineage.orphanedRuns.filter((run) => shownRunIds.has(run.id))
  };
  const stage = plan.stages.find((entry) => time < entry.end) ?? plan.stages.at(-1) ?? null;
  const focusKey = complete
    ? (snapshot.lineage.championRefs[0] ? lineageRefKey(snapshot.lineage.championRefs[0]) : [...visibleKeys].at(-1) ?? null)
    : [...visibleKeys].at(-1) ?? null;
  return {
    complete, stage, lineage, visibleKeys, activeKeys, focusKey,
    nodeDetails: new Map(nodes.map((node) => [lineageRefKey(node.ref), node])),
    runs: complete ? snapshot.runs : snapshot.runs.filter((run) => shownRunIds.has(run.id)),
    stageProgress: stage ? Math.max(0, Math.min(1, (time - stage.start) / (stage.end - stage.start))) : 1
  };
}
